import React, {
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Table, Row, Col } from 'react-bootstrap';
import PropTypes from 'prop-types';
import AppContext from 'context/Context';
import { formatCurrency, formatVariancePercent } from './formatters';

const SUB_COLUMNS = ['Actual', 'Budget', 'Variance', '%Variance'];

const CODE_COL_WIDTH = 80;
const NAME_COL_WIDTH = 190;
const YTD_COL_WIDTH = 125;

const FROZEN_LEFT = {
  code: 0,
  name: CODE_COL_WIDTH,
  ytdBenefit: CODE_COL_WIDTH + NAME_COL_WIDTH,
  ytdBudget: CODE_COL_WIDTH + NAME_COL_WIDTH + YTD_COL_WIDTH,
  ytdVariance: CODE_COL_WIDTH + NAME_COL_WIDTH + YTD_COL_WIDTH * 2,
  ytdVariancePercent: CODE_COL_WIDTH + NAME_COL_WIDTH + YTD_COL_WIDTH * 3
};

const computeYearToDate = periods => {
  if (!periods?.length) return null;
  let benefit = 0;
  let budget = 0;
  let hasData = false;
  periods.forEach(period => {
    if (!period) return;
    if (period.profitAndLoss != null || period.budget != null) hasData = true;
    benefit += period.profitAndLoss || 0;
    budget += period.budget || 0;
  });
  if (!hasData) return null;
  const variance = benefit - budget;
  const variancePercent = budget ? (variance / budget) * 100 : null;
  return { profitAndLoss: benefit, budget, variance, variancePercent };
};

const PeriodFigureCells = ({ figure, cellStyle, onClick }) => {
  const clickableStyle = onClick
    ? { ...cellStyle, cursor: 'pointer' }
    : cellStyle;
  return (
    <>
      <td className="text-end" style={clickableStyle} onClick={onClick}>
        {formatCurrency(figure?.profitAndLoss)}
      </td>
      <td className="text-end" style={cellStyle}>
        {formatCurrency(figure?.budget)}
      </td>
      <td className="text-end" style={cellStyle}>
        {formatCurrency(figure?.variance)}
      </td>
      <td className="text-end" style={cellStyle}>
        {formatVariancePercent(figure?.variancePercent)}
      </td>
    </>
  );
};

PeriodFigureCells.propTypes = {
  figure: PropTypes.shape({
    profitAndLoss: PropTypes.number,
    budget: PropTypes.number,
    variance: PropTypes.number,
    variancePercent: PropTypes.number
  }),
  cellStyle: PropTypes.object.isRequired,
  onClick: PropTypes.func
};

const frozenBodyStyle = (rowStyle, left, extra = {}) => ({
  ...rowStyle,
  position: 'sticky',
  left,
  zIndex: 1,
  whiteSpace: 'nowrap',
  ...extra
});

const YtdFigureCells = ({ figure, rowStyle, onClick, borderColor }) => (
  <>
    <td
      className="text-end"
      style={{
        ...frozenBodyStyle(rowStyle, FROZEN_LEFT.ytdBenefit, {
          width: YTD_COL_WIDTH,
          minWidth: YTD_COL_WIDTH
        }),
        cursor: onClick ? 'pointer' : 'default'
      }}
      onClick={onClick}
    >
      {formatCurrency(figure?.profitAndLoss)}
    </td>
    <td
      className="text-end"
      style={frozenBodyStyle(rowStyle, FROZEN_LEFT.ytdBudget, {
        width: YTD_COL_WIDTH,
        minWidth: YTD_COL_WIDTH
      })}
    >
      {formatCurrency(figure?.budget)}
    </td>
    <td
      className="text-end"
      style={frozenBodyStyle(rowStyle, FROZEN_LEFT.ytdVariance, {
        width: YTD_COL_WIDTH,
        minWidth: YTD_COL_WIDTH
      })}
    >
      {formatCurrency(figure?.variance)}
    </td>
    <td
      className="text-end"
      style={frozenBodyStyle(rowStyle, FROZEN_LEFT.ytdVariancePercent, {
        width: YTD_COL_WIDTH,
        minWidth: YTD_COL_WIDTH,
        borderRight: `2px solid ${borderColor}`
      })}
    >
      {formatVariancePercent(figure?.variancePercent)}
    </td>
  </>
);

YtdFigureCells.propTypes = {
  figure: PropTypes.shape({
    profitAndLoss: PropTypes.number,
    budget: PropTypes.number,
    variance: PropTypes.number,
    variancePercent: PropTypes.number
  }),
  rowStyle: PropTypes.object.isRequired,
  onClick: PropTypes.func,
  borderColor: PropTypes.string.isRequired
};

const DepartmentSpendingMonthDetailTable = ({
  summary,
  periodIndexes,
  isFixedLinesHighlighted = false,
  onCellClick
}) => {
  const {
    config: { isDark }
  } = useContext(AppContext);

  const borderColor = isDark ? '#4a4f58' : '#c4c9d0';

  const headerRow1Ref = useRef(null);
  const [headerRow1Height, setHeaderRow1Height] = useState(0);

  useLayoutEffect(() => {
    const measure = () => {
      if (headerRow1Ref.current) {
        setHeaderRow1Height(
          headerRow1Ref.current.getBoundingClientRect().height
        );
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [summary, periodIndexes, isDark]);

  const headerCellStyle = useMemo(
    () => ({
      backgroundColor: isDark ? 'rgb(34 45 59)' : '#d8e2ef',
      color: isDark ? 'white' : '#000000',
      fontWeight: 'bold',
      fontSize: '0.875rem',
      border: `1px solid ${borderColor}`,
      padding: '8px 12px',
      verticalAlign: 'middle'
    }),
    [isDark, borderColor]
  );

  const headerRow1CellStyle = useMemo(
    () => ({ ...headerCellStyle, position: 'sticky', top: 0, zIndex: 3 }),
    [headerCellStyle]
  );

  const headerRow2CellStyle = useMemo(
    () => ({
      ...headerCellStyle,
      position: 'sticky',
      top: headerRow1Height,
      zIndex: 2
    }),
    [headerCellStyle, headerRow1Height]
  );

  const frozenHeaderStyle = (left, top, extra = {}) => ({
    ...headerCellStyle,
    position: 'sticky',
    top,
    left,
    zIndex: 4,
    whiteSpace: 'nowrap',
    ...extra
  });

  const bodyCellStyle = useMemo(
    () => ({
      border: `1px solid ${borderColor}`,
      padding: '8px 10px',
      fontSize: '0.9375rem',
      color: isDark ? 'white' : 'black'
    }),
    [isDark, borderColor]
  );

  const stripeColors = useMemo(
    () => ({
      even: isDark ? '#1e1e2f' : '#ffffff',
      odd: isDark ? '#262838' : 'aliceblue'
    }),
    [isDark]
  );

  const fixedLineHighlightColor = isDark ? '#5c4a12' : '#fff3cd';

  const totalRowStyle = useMemo(
    () => ({
      ...bodyCellStyle,
      fontWeight: 'bold',
      backgroundColor: isDark ? '#2a3547' : '#eef3f9'
    }),
    [bodyCellStyle, isDark]
  );

  if (!summary || !periodIndexes?.length) return null;

  const { totalRow, rows, periodLabels, periodIds } = summary;
  const selectedLabels = periodIndexes.map(index => periodLabels?.[index]);

  const renderPeriodGroups = (row, rowStyle) =>
    periodIndexes.map((periodIndex, columnIndex) => (
      <PeriodFigureCells
        key={periodIndex}
        figure={row.periods?.[periodIndex]}
        cellStyle={rowStyle}
        onClick={
          onCellClick
            ? () =>
                onCellClick({
                  account: row,
                  periodId: periodIds?.[periodIndex],
                  periodLabel: selectedLabels[columnIndex]
                })
            : undefined
        }
      />
    ));

  const renderYtdCells = (row, rowStyle) => (
    <YtdFigureCells
      figure={computeYearToDate(row.periods)}
      rowStyle={rowStyle}
      borderColor={borderColor}
      onClick={
        onCellClick
          ? () =>
              onCellClick({
                account: row,
                periodId: undefined,
                periodLabel: 'Year To Date'
              })
          : undefined
      }
    />
  );

  return (
    <div className="mt-3">
      <Row className="mb-2">
        <Col>
          <span className="h5">
            Detail — {selectedLabels.filter(Boolean).join(', ')}
          </span>
        </Col>
      </Row>
      <div style={{ maxHeight: '50vh', overflow: 'auto' }}>
        <Table size="sm" className="mb-0">
          <thead>
            <tr ref={headerRow1Ref}>
              <th
                rowSpan={2}
                style={frozenHeaderStyle(FROZEN_LEFT.code, 0, {
                  width: CODE_COL_WIDTH,
                  minWidth: CODE_COL_WIDTH
                })}
              >
                Code
              </th>
              <th
                rowSpan={2}
                style={frozenHeaderStyle(FROZEN_LEFT.name, 0, {
                  width: NAME_COL_WIDTH,
                  minWidth: NAME_COL_WIDTH,
                  whiteSpace: 'normal'
                })}
              >
                Name
              </th>
              <th
                colSpan={4}
                className="text-center"
                style={frozenHeaderStyle(FROZEN_LEFT.ytdBenefit, 0)}
              >
                Year To Date
              </th>
              {selectedLabels.map((label, columnIndex) => (
                <th
                  key={`${label}-${columnIndex}`}
                  colSpan={4}
                  className="text-center"
                  style={headerRow1CellStyle}
                >
                  {label}
                </th>
              ))}
            </tr>
            <tr>
              <th
                className="text-center"
                style={frozenHeaderStyle(FROZEN_LEFT.ytdBenefit, headerRow1Height, {
                  width: YTD_COL_WIDTH,
                  minWidth: YTD_COL_WIDTH
                })}
              >
                Actual
              </th>
              <th
                className="text-center"
                style={frozenHeaderStyle(FROZEN_LEFT.ytdBudget, headerRow1Height, {
                  width: YTD_COL_WIDTH,
                  minWidth: YTD_COL_WIDTH
                })}
              >
                Budget
              </th>
              <th
                className="text-center"
                style={frozenHeaderStyle(FROZEN_LEFT.ytdVariance, headerRow1Height, {
                  width: YTD_COL_WIDTH,
                  minWidth: YTD_COL_WIDTH
                })}
              >
                Variance
              </th>
              <th
                className="text-center"
                style={frozenHeaderStyle(FROZEN_LEFT.ytdVariancePercent, headerRow1Height, {
                  width: YTD_COL_WIDTH,
                  minWidth: YTD_COL_WIDTH,
                  borderRight: `2px solid ${borderColor}`
                })}
              >
                %Variance
              </th>
              {periodIndexes.map(periodIndex =>
                SUB_COLUMNS.map(col => (
                  <th
                    key={`${periodIndex}-${col}`}
                    className="text-center"
                    style={headerRow2CellStyle}
                  >
                    {col}
                  </th>
                ))
              )}
            </tr>
          </thead>
          <tbody>
            {totalRow && (
              <tr>
                <td style={frozenBodyStyle(totalRowStyle, FROZEN_LEFT.code, { width: CODE_COL_WIDTH, minWidth: CODE_COL_WIDTH })}>
                  {totalRow.code}
                </td>
                <td
                  style={frozenBodyStyle(totalRowStyle, FROZEN_LEFT.name, {
                    width: NAME_COL_WIDTH,
                    minWidth: NAME_COL_WIDTH,
                    whiteSpace: 'normal'
                  })}
                >
                  {totalRow.name}
                </td>
                {renderYtdCells(totalRow, totalRowStyle)}
                {renderPeriodGroups(totalRow, totalRowStyle)}
              </tr>
            )}
            {(rows || []).map((row, rowIdx) => {
              const stripeColor =
                rowIdx % 2 === 0 ? stripeColors.even : stripeColors.odd;
              const rowStyle = {
                ...bodyCellStyle,
                backgroundColor:
                  isFixedLinesHighlighted && row.isFixed
                    ? fixedLineHighlightColor
                    : stripeColor
              };
              return (
                <tr key={row.code}>
                  <td style={frozenBodyStyle(rowStyle, FROZEN_LEFT.code, { width: CODE_COL_WIDTH, minWidth: CODE_COL_WIDTH })}>
                    {row.code}
                  </td>
                  <td
                    style={frozenBodyStyle(rowStyle, FROZEN_LEFT.name, {
                      width: NAME_COL_WIDTH,
                      minWidth: NAME_COL_WIDTH,
                      whiteSpace: 'normal'
                    })}
                  >
                    {row.name}
                  </td>
                  {renderYtdCells(row, rowStyle)}
                  {renderPeriodGroups(row, rowStyle)}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

DepartmentSpendingMonthDetailTable.propTypes = {
  summary: PropTypes.shape({
    periodLabels: PropTypes.arrayOf(PropTypes.string),
    periodIds: PropTypes.arrayOf(PropTypes.number),
    totalRow: PropTypes.object,
    rows: PropTypes.arrayOf(PropTypes.object)
  }),
  periodIndexes: PropTypes.arrayOf(PropTypes.number),
  isFixedLinesHighlighted: PropTypes.bool,
  onCellClick: PropTypes.func
};

export default DepartmentSpendingMonthDetailTable;
